import AppBarSpacer from '../components/common/AppBarSpacer';
import {
  AddCircleOutline,
  CheckCircleOutline,
  ContentCopy,
  DeleteOutline,
  EditOutlined,
  ListAltOutlined,
  MoreVert,
  Search,
  Share,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import MiniDrawer from '../components/MiniDrawer';
import PageHeader from '../components/common/PageHeader';
import { notifyAPIClient } from '../utils/toast';
import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableActionCell,
  ManagementTableHeaderCell,
  managementTableRowSx,
} from '../components/common/ManagementTable';
import {
  CustomerIntakeField,
  CustomerIntakeFormConfig,
  CustomerIntakeServiceBinding,
  CustomerIntakeTemplateStatus,
  CustomerIntakeTemplateVersion,
  getCustomerIntakeFieldDescription,
} from '../types/CustomerIntake.type';
import {
  ServiceType,
  Subsubtype,
  Subtype,
} from '../types/ServiceAccordion.type';
import {
  activateCustomerIntakeTemplate,
  createCustomerIntakeForm,
  createCustomerIntakeTemplate,
  createEmptyCustomerIntakeTemplate,
  CustomerIntakeForm,
  deleteCustomerIntakeTemplate,
  getCustomerIntakeErrorMessage,
  getCustomerIntakeTemplate,
  listCustomerIntakeForms,
  listCustomerIntakeTemplates,
  updateCustomerIntakeTemplate,
} from '../utils/customerIntakeApi';
import PackageSelectorDialog from '../components/CustomerIntake/PackageSelectorDrawer';
import CustomerIntakeServiceField from '../components/CustomerIntake/CustomerIntakeServiceField';
import { useTranslation } from '../i18n';
import CustomerIntakeDefinitionEditor, {
  CustomerIntakeCustomTypeTarget,
  applyPackageNameDefaults,
  importPackageConfigurationIntoDefinition,
} from '../components/CustomerIntake/CustomerIntakeDefinitionEditor';

const cloneTemplate = (
  template: CustomerIntakeTemplateVersion,
): CustomerIntakeTemplateVersion =>
  JSON.parse(JSON.stringify(template)) as CustomerIntakeTemplateVersion;

const createId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const statusColor: Record<CustomerIntakeTemplateStatus, 'default' | 'success'> =
  {
    DRAFT: 'default',
    ACTIVE: 'success',
  };

const formatUpdatedAt = (date: string) =>
  new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const getNextVersion = (templates: CustomerIntakeTemplateVersion[]) => {
  const highestMinor = templates.reduce((highest, template) => {
    const [, minorPart = '0'] = template.version.split('.');
    return Math.max(highest, Number(minorPart) || 0);
  }, 0);

  return `1.${highestMinor + 1}`;
};

/**
 * Strips any copy suffix from a name so repeated cloning doesn't accumulate
 * endless "- Copy" / "(copy N)" chains. Handles both the legacy " - Copy"
 * append style and the "(copy N)" style.
 */
const stripCopySuffix = (name: string) =>
  name
    .trim()
    .replace(/\s*\(copy\s+\d+\)\s*$/i, '')
    .replace(/(\s*-\s*copy)+\s*$/i, '')
    .trim();

/**
 * Builds the next available "<base> (copy N)" name for a clone, incrementing N
 * past any existing copies of the same base among the given templates.
 */
const getNextCopyName = (
  sourceName: string,
  templates: CustomerIntakeTemplateVersion[],
) => {
  const base = stripCopySuffix(sourceName);
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const copyPattern = new RegExp(`^${escaped}\\s*\\(copy\\s+(\\d+)\\)\\s*$`, 'i');
  const highest = templates.reduce((max, template) => {
    const match = template.name.trim().match(copyPattern);
    return match ? Math.max(max, Number(match[1]) || 0) : max;
  }, 1);
  return `${base} (copy ${highest + 1})`;
};

const CUSTOMER_SURVEY_FORM_PATH = '/customer-survey/form';
const CUSTOMER_SURVEY_QR_PATH = '/customer-survey/qr';

const CustomerIntakeFormPreview = ({
  form,
}: {
  form: CustomerIntakeFormConfig;
}) => {
  const [linkCopied, setLinkCopied] = useState(false);

  const shareUrl = new URL(
    CUSTOMER_SURVEY_FORM_PATH,
    window.location.origin,
  ).toString();
  const qrUrl = new URL(
    CUSTOMER_SURVEY_QR_PATH,
    window.location.origin,
  ).toString();

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
    }
  };

  const fieldLabel = (field: CustomerIntakeField) =>
    `${field.label}${field.required ? ' *' : ''}`;

  const renderPreviewField = (field: CustomerIntakeField) => {
    const helperText = [
      getCustomerIntakeFieldDescription(field.description),
      field.conditional ? 'Shown conditionally in the public form.' : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    if (field.type === 'service') {
      return (
        <CustomerIntakeServiceField
          key={field.id}
          field={field}
          value={field.service?.multiple ? [] : ''}
          label={fieldLabel(field)}
          helperText={helperText || undefined}
          disabled
          onChange={() => undefined}
        />
      );
    }

    if (
      field.type === 'text' ||
      field.type === 'custom' ||
      field.type === 'phone' ||
      field.type === 'email' ||
      field.type === 'date' ||
      field.type === 'number' ||
      field.type === 'textarea'
    ) {
      return (
        <TextField
          key={field.id}
          fullWidth
          disabled
          label={fieldLabel(field)}
          type={
            field.type === 'phone'
              ? 'tel'
              : field.type === 'email'
                ? 'email'
                : field.type === 'date'
                  ? 'date'
                  : field.type === 'number'
                    ? 'number'
                    : 'text'
          }
          placeholder={field.placeholder}
          helperText={helperText || undefined}
          multiline={field.type === 'textarea'}
          minRows={field.type === 'textarea' ? 3 : undefined}
          InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
        />
      );
    }

    if (field.type === 'select') {
      return (
        <FormControl key={field.id} fullWidth disabled>
          <InputLabel id={`preview-${field.id}-label`}>
            {fieldLabel(field)}
          </InputLabel>
          <Select
            labelId={`preview-${field.id}-label`}
            value=""
            label={fieldLabel(field)}
          >
            <MenuItem value="">
              <em>Select an option</em>
            </MenuItem>
            {(field.options || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );
    }

    if (field.type === 'radio') {
      return (
        <FormControl
          key={field.id}
          component="fieldset"
          disabled
          sx={{ width: '100%' }}
        >
          <FormLabel component="legend">{fieldLabel(field)}</FormLabel>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
          <RadioGroup>
            {(field.options || []).map((option) => (
              <FormControlLabel
                key={option.value}
                disabled
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <FormControl
          key={field.id}
          component="fieldset"
          disabled
          sx={{ width: '100%' }}
        >
          <FormLabel component="legend">{fieldLabel(field)}</FormLabel>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
          <Stack spacing={0.25}>
            {(field.options || []).map((option) => (
              <FormControlLabel
                key={option.value}
                disabled
                control={<Checkbox />}
                label={option.label}
              />
            ))}
          </Stack>
        </FormControl>
      );
    }

    return (
      <Box key={field.id}>
        <FormControlLabel
          disabled
          control={<Checkbox />}
          label={fieldLabel(field)}
        />
        {helperText && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
            {helperText}
          </Typography>
        )}
      </Box>
    );
  };

  const consentFields = form.sections.flatMap((section) =>
    section.fields.filter((field) => field.type === 'consent'),
  );
  const consentSection =
    form.sections.find((section) => section.id === 'confirmation') ||
    form.sections.find((section) =>
      section.fields.some((field) => field.type === 'consent'),
    );

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        borderRadius: 1,
        p: { xs: 1, sm: 2 },
      }}
    >
      <Card sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography
              variant="overline"
              color="primary.main"
              fontWeight={700}
            >
              Aquatics HUB
            </Typography>
            <Typography variant="h5" color="secondary.main" fontWeight={700}>
              {form.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {form.description}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Estimated completion time: {form.estimatedMinutes} minutes.
            </Typography>
          </Box>

          <Divider />

          {form.sections.map((section) => {
            const visibleFields = section.fields.filter(
              (field) => field.type !== 'consent',
            );

            if (visibleFields.length === 0) return null;

            return (
              <Box key={section.id} component="section">
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    color="secondary.main"
                    fontWeight={700}
                  >
                    {section.title}
                  </Typography>
                  {section.description && (
                    <Typography variant="body2" color="text.secondary">
                      {section.description}
                    </Typography>
                  )}
                </Stack>
                <Stack spacing={2.5}>
                  {visibleFields.map(renderPreviewField)}
                </Stack>
              </Box>
            );
          })}

          {consentFields.length > 0 && (
            <Box component="section" sx={{ pt: 1 }}>
              <Divider sx={{ mb: 2.5 }} />
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <Typography
                  variant="subtitle1"
                  color="secondary.main"
                  fontWeight={700}
                >
                  {consentSection?.title || 'Confirmation'}
                </Typography>
                {consentSection?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {consentSection.description}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={2.5}>
                {consentFields.map(renderPreviewField)}
              </Stack>
            </Box>
          )}

          <Button variant="contained" disabled fullWidth>
            Submit information
          </Button>
        </Stack>
      </Card>

      <Card
        variant="outlined"
        sx={{ maxWidth: 760, mx: 'auto', mt: 2, p: { xs: 2, sm: 2.5 } }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Share this form
            </Typography>
            <Typography variant="caption" color="text.secondary">
              These always open the currently Active version for guests to
              submit.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              variant="outlined"
              onClick={() => window.open(qrUrl, '_blank', 'noopener')}
            >
              Open QR page
            </Button>
            <Button variant="outlined" onClick={handleCopyShareLink}>
              {linkCopied ? 'Link copied' : 'Copy sharable link'}
            </Button>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ wordBreak: 'break-all' }}
          >
            {shareUrl}
          </Typography>
        </Stack>
      </Card>
    </Box>
  );
};

type TemplateEditorMode = 'create' | 'edit';
type TemplateEditorTab = 'edit' | 'preview';
const CustomerIntakeTemplates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [templates, setTemplates] = useState<CustomerIntakeTemplateVersion[]>(
    [],
  );
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState('');
  const [draft, setDraft] = useState<CustomerIntakeTemplateVersion | null>(
    null,
  );
  const [editorMode, setEditorMode] = useState<TemplateEditorMode>('edit');
  const [editorTab, setEditorTab] = useState<TemplateEditorTab>('edit');
  const [shareMenuAnchor, setShareMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [editorDialogOpen, setEditorDialogOpen] = useState(false);
  const [activationDialogOpen, setActivationDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<CustomerIntakeTemplateVersion | null>(null);
  const [packageSelectorOpen, setPackageSelectorOpen] = useState(false);
  const [packageTarget, setPackageTarget] =
    useState<CustomerIntakeCustomTypeTarget | null>(null);

  // Selected form scopes the versions list below. A ref mirrors it so the many
  // existing refreshTemplates() callers pick up the current form without needing
  // the id threaded through each call.
  const [forms, setForms] = useState<CustomerIntakeForm[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const selectedFormIdRef = useRef<number | null>(null);
  const [newFormDialogOpen, setNewFormDialogOpen] = useState(false);
  const [newFormName, setNewFormName] = useState('');

  // Left sidebar: search over form types.
  const [formSearch, setFormSearch] = useState('');
  // Version table: search + status filter.
  const [versionSearch, setVersionSearch] = useState('');
  const [versionStatusFilter, setVersionStatusFilter] = useState<
    'all' | CustomerIntakeTemplateStatus
  >('all');
  // Per-row overflow action menu.
  const [rowMenuAnchor, setRowMenuAnchor] = useState<HTMLElement | null>(null);
  const [rowMenuTemplate, setRowMenuTemplate] =
    useState<CustomerIntakeTemplateVersion | null>(null);

  useEffect(() => {
    selectedFormIdRef.current = selectedFormId;
  }, [selectedFormId]);

  const refreshForms = useCallback(async () => {
    try {
      const list = await listCustomerIntakeForms();
      setForms(list);
      setSelectedFormId((current) =>
        current && list.some((form) => form.id === current)
          ? current
          : (list[0]?.id ?? null),
      );
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(error, 'Unable to load forms.'),
      );
    }
  }, []);

  useEffect(() => {
    void refreshForms();
  }, [refreshForms]);

  const refreshTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const formId = selectedFormIdRef.current;
      const result = await listCustomerIntakeTemplates(
        formId ? { formId } : undefined,
      );
      setTemplates(result.items);
      setSelectedId((currentId) =>
        result.items.some((template) => template.id === currentId)
          ? currentId
          : result.items.find((template) => template.status === 'ACTIVE')?.id ||
            result.items[0]?.id ||
            '',
      );
      setPageError('');
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to load customer intake form versions.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshTemplates();
    // Re-fetch whenever the active form tab changes (ref is already synced).
  }, [refreshTemplates, selectedFormId]);

  const handleViewResponses = (templateId: string) => {
    navigate(`/customer-survey/templates/${templateId}/responses`);
  };

  // Each form has its own public token, so the share link + QR carry ?token=…
  // to resolve that specific form (falls back to the default form when absent).
  const selectedForm = forms.find((form) => form.id === selectedFormId) || null;
  const buildShareUrl = (path: string) => {
    const url = new URL(path, window.location.origin);
    if (selectedForm?.publicToken) {
      url.searchParams.set('token', selectedForm.publicToken);
    }
    return url.toString();
  };
  const shareFormUrl = buildShareUrl(CUSTOMER_SURVEY_FORM_PATH);
  const shareQrUrl = buildShareUrl(CUSTOMER_SURVEY_QR_PATH);

  const closeShareMenu = () => setShareMenuAnchor(null);

  const openRowMenu = (
    event: ReactMouseEvent<HTMLElement>,
    template: CustomerIntakeTemplateVersion,
  ) => {
    setRowMenuTemplate(template);
    setRowMenuAnchor(event.currentTarget);
  };
  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuTemplate(null);
  };

  // Sidebar: form types filtered by search.
  const filteredForms = forms.filter((form) =>
    form.name.toLowerCase().includes(formSearch.trim().toLowerCase()),
  );

  // Version table: filtered by search (name/public title) + status.
  const versionQuery = versionSearch.trim().toLowerCase();
  const filteredTemplates = templates.filter((template) => {
    const matchesStatus =
      versionStatusFilter === 'all' || template.status === versionStatusFilter;
    const matchesQuery =
      !versionQuery ||
      template.name.toLowerCase().includes(versionQuery) ||
      (template.title || '').toLowerCase().includes(versionQuery);
    return matchesStatus && matchesQuery;
  });

  const handleCreateForm = async () => {
    const name = newFormName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const created = await createCustomerIntakeForm(name);
      setNewFormDialogOpen(false);
      setNewFormName('');
      await refreshForms();
      setSelectedFormId(created.id);
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(error, 'Unable to create the form.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCopyFormLink = async () => {
    try {
      await navigator.clipboard.writeText(shareFormUrl);
      notifyAPIClient('Form link copied to clipboard.');
    } catch {
      notifyAPIClient('Unable to copy the form link.');
    }
    closeShareMenu();
  };

  const handleOpenQrPage = () => {
    window.open(shareQrUrl, '_blank', 'noopener');
    closeShareMenu();
  };

  const handleEditTemplate = async (templateId: string) => {
    const selectedTemplate = templates.find(
      (template) => template.id === templateId,
    );
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      const detail = await getCustomerIntakeTemplate(templateId);
      setSelectedId(templateId);
      setDraft(cloneTemplate(detail));
      setEditorMode('edit');
      setEditorTab('edit');
      setEditorDialogOpen(true);
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to load the form version.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCloneTemplate = async (
    sourceTemplate: CustomerIntakeTemplateVersion,
  ) => {
    setSaving(true);
    try {
      const sourceDetail = await getCustomerIntakeTemplate(sourceTemplate.id);
      const clonedTemplate = await createCustomerIntakeTemplate(
        {
          ...sourceDetail,
          name: getNextCopyName(sourceDetail.name, templates),
        },
        {
          cloneFromVersionId: sourceDetail.id,
          formId: selectedFormId ?? undefined,
        },
      );

      setSelectedId(clonedTemplate.id);
      setDraft(cloneTemplate(clonedTemplate));
      setEditorMode('edit');
      setEditorTab('edit');
      setEditorDialogOpen(true);
      setPageError('');
      await refreshTemplates();
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to clone the form version.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleActivateTemplate = async (templateId: string) => {
    const selectedTemplate = templates.find(
      (template) => template.id === templateId,
    );
    if (!selectedTemplate || selectedTemplate.status !== 'DRAFT') return;

    setSaving(true);
    try {
      const detail = await getCustomerIntakeTemplate(templateId);
      setSelectedId(templateId);
      setDraft(cloneTemplate(detail));
      setActivationDialogOpen(true);
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to load the form version for activation.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    const selectedTemplate = templates.find(
      (template) => template.id === templateId,
    );
    if (!selectedTemplate || selectedTemplate.status === 'ACTIVE') return;

    setTemplateToDelete(selectedTemplate);
    setDeleteDialogOpen(true);
  };

  const deleteSelectedTemplate = async () => {
    if (!templateToDelete || templateToDelete.status === 'ACTIVE') return;

    setSaving(true);
    try {
      await deleteCustomerIntakeTemplate(templateToDelete.id);
      await refreshTemplates();
      setTemplateToDelete(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to delete the form version.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const closeEditor = () => {
    setEditorDialogOpen(false);
    setDraft(null);
    setEditorTab('edit');
    setPackageSelectorOpen(false);
    setPackageTarget(null);
  };

  const handleAddCustomType = () => {
    setPackageTarget(null);
    setPackageSelectorOpen(true);
  };

  const handlePackageSelected = async (
    packageItem: Subsubtype,
    configuration: CustomerIntakeFormConfig | null,
    context: { serviceType: ServiceType; subtype: Subtype },
  ) => {
    if (!draft || !configuration) {
      throw new Error('This package has no form configuration.');
    }

    const packageConfiguration = applyPackageNameDefaults(
      configuration,
      packageItem.name,
    );
    const nextDefinition = importPackageConfigurationIntoDefinition(
      draft,
      packageConfiguration,
      packageItem.id,
      packageTarget || undefined,
      {
        serviceTypeId: Number(context.serviceType.id),
        subtypeId: Number(context.subtype.id),
        packageId: Number(packageItem.id),
        packageName: packageItem.name,
      } satisfies CustomerIntakeServiceBinding,
    );

    setDraft((currentDraft) =>
      currentDraft
        ? {
            ...currentDraft,
            ...nextDefinition,
          }
        : currentDraft,
    );
    setPackageSelectorOpen(false);
    setPackageTarget(null);
  };

  const saveDraft = async (templateToSave = draft) => {
    if (!templateToSave) return;

    setSaving(true);
    try {
      const savedTemplate =
        editorMode === 'create'
          ? await createCustomerIntakeTemplate(templateToSave, {
              formId: selectedFormId ?? undefined,
            })
          : await updateCustomerIntakeTemplate(templateToSave);
      setDraft(cloneTemplate(savedTemplate));
      setSelectedId(savedTemplate.id);
      setEditorMode('edit');
      setEditorDialogOpen(false);
      await refreshTemplates();
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to save the form version.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const createVersion = async () => {
    setSaving(true);
    try {
      const baseTemplate = createEmptyCustomerIntakeTemplate();
      const version = getNextVersion(templates);
      const newTemplate: CustomerIntakeTemplateVersion = {
        ...cloneTemplate(baseTemplate),
        id: createId('customer-intake-template'),
        name: `Customer information registration - Draft ${version}`,
        version,
        status: 'DRAFT',
        isLocked: false,
        responseCount: 0,
        updatedAt: new Date().toISOString(),
      };
      setSelectedId(newTemplate.id);
      setDraft(cloneTemplate(newTemplate));
      setEditorMode('create');
      setEditorTab('edit');
      setEditorDialogOpen(true);
      setPageError('');
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to prepare a new form version.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const activateSelectedTemplate = async () => {
    if (!draft) return;

    if (draft.status !== 'DRAFT') {
      setPageError(
        'Only Draft customer intake form versions can be activated.',
      );
      setActivationDialogOpen(false);
      return;
    }

    setSaving(true);
    try {
      const savedTemplate =
        editorMode === 'create'
          ? await createCustomerIntakeTemplate(draft, {
              formId: selectedFormId ?? undefined,
            })
          : await updateCustomerIntakeTemplate(draft);
      const activatedTemplate = await activateCustomerIntakeTemplate(
        savedTemplate.id,
      );
      setDraft(cloneTemplate(activatedTemplate));
      setSelectedId(activatedTemplate.id);
      setActivationDialogOpen(false);
      setEditorDialogOpen(false);
      setEditorMode('edit');
      await refreshTemplates();
    } catch (error) {
      setPageError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to activate the form version.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const updateDraft = (update: Partial<CustomerIntakeTemplateVersion>) => {
    setDraft((currentDraft) =>
      currentDraft ? { ...currentDraft, ...update } : currentDraft,
    );
  };

  return (
    <div className="flex h-screen">
      <MiniDrawer open={open} setOpen={setOpen} />
      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title={t('formBuilder.pageTitle')}
            subtitle={t('formBuilder.pageSubtitle')}
          />

          {pageError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {pageError}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Master-detail: form types in a searchable sidebar, versions on the right. */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {/* Sidebar — each form has its own public link + QR code. */}
              <Paper
                sx={{
                  width: 280,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ p: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('formBuilder.searchForms')}
                    value={formSearch}
                    onChange={(event) => setFormSearch(event.target.value)}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
                <Divider />
                <List
                  dense
                  sx={{ maxHeight: '60vh', overflowY: 'auto', flex: 1, py: 0 }}
                >
                  {filteredForms.map((form) => (
                    <ListItemButton
                      key={form.id}
                      selected={form.id === selectedFormId}
                      onClick={() => setSelectedFormId(form.id)}
                    >
                      <ListItemText
                        primary={form.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          noWrap: true,
                        }}
                      />
                      <Chip
                        size="small"
                        label={form.versionCount}
                        sx={{ ml: 1 }}
                      />
                    </ListItemButton>
                  ))}
                  {filteredForms.length === 0 && (
                    <Box sx={{ px: 2, py: 3 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        {t('formBuilder.noFormsFound')}
                      </Typography>
                    </Box>
                  )}
                </List>
                <Divider />
                <Button
                  fullWidth
                  startIcon={<AddCircleOutline />}
                  onClick={() => setNewFormDialogOpen(true)}
                  sx={{ borderRadius: 0, py: 1.25 }}
                >
                  {t('formBuilder.newForm')}
                </Button>
              </Paper>

              {/* Right panel: version toolbar + table */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <TextField
                        size="small"
                        placeholder={t('formBuilder.searchVersions')}
                        value={versionSearch}
                        onChange={(event) =>
                          setVersionSearch(event.target.value)
                        }
                        sx={{ minWidth: 220 }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search fontSize="small" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value={versionStatusFilter}
                          onChange={(event) =>
                            setVersionStatusFilter(
                              event.target.value as
                                | 'all'
                                | CustomerIntakeTemplateStatus,
                            )
                          }
                        >
                          <MenuItem value="all">
                            {t('formBuilder.allStatuses')}
                          </MenuItem>
                          <MenuItem value="ACTIVE">
                            {t('formBuilder.statusACTIVE')}
                          </MenuItem>
                          <MenuItem value="DRAFT">
                            {t('formBuilder.statusDRAFT')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddCircleOutline />}
                      onClick={createVersion}
                      disabled={loading || saving || selectedFormId === null}
                      className="bg-blue-600 hover:bg-blue-700 !text-white font-semibold py-2 px-4 rounded"
                    >
                      {t('formBuilder.addNewFormVersion')}
                    </Button>
                  </Box>

                  <Paper sx={{ overflow: 'hidden' }}>
              <Stack spacing={2}>
                <Box sx={{ px: 2, pt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('formBuilder.onlyOneActive')}
                  </Typography>
                </Box>
                <Divider />
                <TableContainer>
                  <Table
                    size="small"
                    padding="checkbox"
                    sx={{ minWidth: 640 }}
                    aria-label="Form versions"
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR,
                        }}
                      >
                        <ManagementTableHeaderCell>
                          {t('formBuilder.colTemplateName')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('formBuilder.colPublicTitle')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('formBuilder.colStatus')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('formBuilder.colLastUpdated')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('formBuilder.colResponses')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell sticky>
                          {t('formBuilder.colActions')}
                        </ManagementTableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTemplates.map((template) => (
                        <TableRow
                          key={template.id}
                          sx={managementTableRowSx(selectedId === template.id)}
                        >
                          <TableCell>
                            <Tooltip title={template.name}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ maxWidth: 320 }}
                              >
                                {template.name}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={template.title || ''}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ maxWidth: 220 }}
                              >
                                {template.title}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Chip
                              size="small"
                              label={t(`formBuilder.status${template.status}`)}
                              color={statusColor[template.status]}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" color="text.secondary">
                              {formatUpdatedAt(template.updatedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography
                              variant="body2"
                              color="primary.main"
                              fontWeight={700}
                            >
                              {template.responseCount}
                            </Typography>
                          </TableCell>
                          <ManagementTableActionCell
                            selected={selectedId === template.id}
                          >
                            <Tooltip title={t('formBuilder.editVersion')}>
                              <span>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  aria-label={`Edit version ${template.version}`}
                                  onClick={() =>
                                    void handleEditTemplate(template.id)
                                  }
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                template.status === 'ACTIVE'
                                  ? 'Active version'
                                  : t('formBuilder.activateVersion')
                              }
                            >
                              <span>
                                <IconButton
                                  color="success"
                                  size="small"
                                  aria-label={`Activate version ${template.version}`}
                                  disabled={template.status !== 'DRAFT'}
                                  onClick={() =>
                                    handleActivateTemplate(template.id)
                                  }
                                >
                                  <CheckCircleOutline fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t('formBuilder.moreActions')}>
                              <IconButton
                                size="small"
                                aria-label={`More actions for version ${template.version}`}
                                onClick={(event) => openRowMenu(event, template)}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ManagementTableActionCell>
                        </TableRow>
                      ))}
                      {filteredTemplates.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              {loading
                                ? 'Loading form versions...'
                                : templates.length === 0
                                  ? 'No form versions found.'
                                  : t('formBuilder.noMatchingVersions')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                  </Stack>
                </Paper>
                </Stack>
              </Box>
            </Box>

            <Menu
              anchorEl={shareMenuAnchor}
              open={Boolean(shareMenuAnchor)}
              onClose={closeShareMenu}
            >
              <MenuItem onClick={handleCopyFormLink}>Copy form link</MenuItem>
              <MenuItem onClick={handleOpenQrPage}>Open QR page</MenuItem>
            </Menu>

            {/* Per-row overflow actions */}
            <Menu
              anchorEl={rowMenuAnchor}
              open={Boolean(rowMenuAnchor)}
              onClose={closeRowMenu}
            >
              <MenuItem
                onClick={() => {
                  if (rowMenuTemplate) handleViewResponses(rowMenuTemplate.id);
                  closeRowMenu();
                }}
              >
                <ListAltOutlined fontSize="small" style={{ marginRight: 8 }} />
                {t('formBuilder.viewResponses')}
              </MenuItem>
              <MenuItem
                disabled={loading || saving}
                onClick={() => {
                  if (rowMenuTemplate) void handleCloneTemplate(rowMenuTemplate);
                  closeRowMenu();
                }}
              >
                <ContentCopy fontSize="small" style={{ marginRight: 8 }} />
                {t('formBuilder.cloneVersion')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCopyFormLink();
                  closeRowMenu();
                }}
              >
                <Share fontSize="small" style={{ marginRight: 8 }} />
                Copy form link
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleOpenQrPage();
                  closeRowMenu();
                }}
              >
                <Share fontSize="small" style={{ marginRight: 8 }} />
                Open QR page
              </MenuItem>
              <Divider />
              <MenuItem
                disabled={rowMenuTemplate?.status === 'ACTIVE'}
                onClick={() => {
                  if (rowMenuTemplate) handleDeleteTemplate(rowMenuTemplate.id);
                  closeRowMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteOutline fontSize="small" style={{ marginRight: 8 }} />
                {t('formBuilder.deleteVersion')}
              </MenuItem>
            </Menu>

            <Dialog
              fullWidth
              maxWidth="lg"
              open={editorDialogOpen}
              onClose={closeEditor}
              scroll="paper"
            >
              <DialogTitle>
                {editorMode === 'create'
                  ? 'Create Form version'
                  : `Edit Form version${draft ? ` ${draft.version}` : ''}`}
              </DialogTitle>
              <Tabs
                value={editorTab}
                onChange={(_, value) =>
                  setEditorTab(value as TemplateEditorTab)
                }
                aria-label="Form version editor tabs"
                sx={{
                  px: 3,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Tab value="edit" label="Edit Form version" />
                <Tab value="preview" label="Preview" />
              </Tabs>
              <DialogContent dividers>
                {draft ? (
                  editorTab === 'preview' ? (
                    <CustomerIntakeFormPreview form={draft} />
                  ) : (
                    <Stack spacing={1.5}>
                      <Card className="customer-intake-card" sx={{ p: 1.5 }}>
                        <Stack spacing={1.5}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            spacing={1}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography
                                variant="h6"
                                color="secondary.main"
                                fontWeight={700}
                              >
                                Version {draft.version}
                              </Typography>
                              <Chip
                                size="small"
                                label={t(`formBuilder.status${draft.status}`)}
                                color={statusColor[draft.status]}
                              />
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {draft.status === 'ACTIVE'
                                ? 'This version is currently shown to guests.'
                                : draft.isLocked
                                  ? 'Previously Active version. It is not shown to guests until activated again.'
                                  : 'This version is not shown to guests yet.'}
                            </Typography>
                          </Stack>
                          <Divider />
                          <TextField
                            fullWidth
                            size="small"
                            label={t('formBuilder.templateNameLabel')}
                            value={draft.name}
                            onChange={(event) =>
                              updateDraft({ name: event.target.value })
                            }
                          />
                        </Stack>
                      </Card>

                      <CustomerIntakeDefinitionEditor
                        value={draft}
                        mode="form-version"
                        onAddCustomType={handleAddCustomType}
                        onChange={(nextDefinition) =>
                          updateDraft(nextDefinition)
                        }
                      />
                    </Stack>
                  )
                ) : (
                  <Alert severity="warning">
                    Select a template version to edit.
                  </Alert>
                )}
              </DialogContent>
              <DialogActions
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 1,
                  backgroundColor: 'background.paper',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  px: 3,
                  py: 1.5,
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Button onClick={closeEditor} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => saveDraft()}
                  disabled={!draft || saving}
                >
                  Save changes
                </Button>
                {draft && draft.status !== 'ACTIVE' && (
                  <Button
                    variant="contained"
                    onClick={() => setActivationDialogOpen(true)}
                    disabled={saving}
                  >
                    Activate version
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          </Stack>
        </div>
      </main>

      <Dialog
        open={activationDialogOpen}
        onClose={() => setActivationDialogOpen(false)}
      >
        <DialogTitle>{t('formBuilder.activateDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Activating version {draft?.version} will move the current Active
            version to Draft. Only version {draft?.version} will be shown to
            guests.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivationDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void activateSelectedTemplate()}
            disabled={saving}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
      >
        <DialogTitle>Delete form version?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Version {templateToDelete?.version} will be removed. Active and
            previously Active versions cannot be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setTemplateToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void deleteSelectedTemplate()}
            disabled={saving}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <PackageSelectorDialog
        open={packageSelectorOpen}
        onClose={() => {
          setPackageSelectorOpen(false);
          setPackageTarget(null);
        }}
        onSelectPackage={handlePackageSelected}
      />

      <Dialog
        open={newFormDialogOpen}
        onClose={() => setNewFormDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('formBuilder.newFormDialogTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Form name"
            placeholder="e.g. October Camp"
            value={newFormName}
            onChange={(event) => setNewFormName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void handleCreateForm();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFormDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateForm}
            disabled={saving || newFormName.trim() === ''}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CustomerIntakeTemplates;
